<script setup lang="ts">
import {Pencil} from "@lucide/vue";
import {LinePosology} from "@/medication-checkup/services/LinePosologies";

/**
 * The posology of a line in words, wherever the review lists the line: on
 * a prescription, among the counter purchases, among the treatments
 * retained, in the checklist of the step.
 *
 * A retained line with no posology asks for one — a line left out asks for
 * nothing; one the pharmacist changed from what was read says so. The
 * pencil opens the dosage dialog — the posology is never typed in place.
 */

defineProps<{
    posology: LinePosology;
    /**
     * Whether the pencil is offered: a frozen record is read only.
     */
    editable?: boolean;
    /**
     * Whether the line is retained: only a retained line asks for its
     * posology and offers the pencil.
     */
    asked?: boolean;
    /**
     * Smaller type, for the checklist beside the thread.
     */
    dense?: boolean;
}>();

const emit = defineEmits<{edit: []}>();
</script>

<template>
  <span class="block leading-snug" :class="dense ? 'text-[10px]' : 'text-[11px]'">
    <span v-if="posology.text" class="text-gray-dark/80">{{ posology.text }}</span>
    <button
      v-else-if="editable && asked"
      type="button"
      class="rounded-md border border-dashed border-red-light/60 px-1.5 py-px font-medium text-red-light hover:bg-red-light/5"
      @click.stop="emit('edit')"
    >
      Posologie à renseigner
    </button>
    <span v-else-if="!editable" class="italic text-muted-foreground">Posologie non renseignée</span>

    <span
      v-if="posology.edited"
      class="ml-1.5 inline-block rounded-full bg-orange/35 px-1.5 align-middle text-[9px] font-bold text-amber-800"
    >
      modifiée
    </span>

    <button
      v-if="editable && asked"
      type="button"
      class="ml-1.5 inline-flex align-middle text-primary hover:opacity-70"
      title="Modifier la posologie"
      @click.stop="emit('edit')"
    >
      <Pencil class="size-3.5" />
    </button>
  </span>
</template>
